import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
  KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import CustomDateRangePicker from 'general/components/Others/CustomDateRangePicker';
import AppColors from 'general/constants/AppColors';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import useWindowSize from 'hooks/useWindowSize';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

SystemLogListView.propTypes = {};

const defaultColumnIds = [
  'NOTIFICATION_CODE',
  'TITLE',
  'CONTENT',
  'RECEIVER',
  'CREATED_AT',
  'CREATED_BY',
  'LINK',
  'CATEGORY',
  'STATUS',
  'TIME'
];

function SystemLogListView(props) {
  // MARK: ---- PROPERTIES ----
  const router = useRouter();
  const { user } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const prefKey = useMemo(() => {
    const userId = user.userId;
    const pathName = router.pathname;
    const key = `${userId}_${pathName}`;
    return key;
  }, [user, router]);
  const [filter, setFilter] = useState(ChargeHelper.Filter.systemLogList);
  const [systemLogList, setSystemLogList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  useEffect(() => {
    setIsLoading(true);
    const params = _.pickBy(
      filter,
      (value) => value !== null && value !== undefined && value !== ''
    );
    ChargeHelper.API.Notification.requestGetNotificationList(params)
      .then((response) => {
        setSystemLogList(response?.notifications);
        setTotal(response?.total);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filter]);
  const fullColumns = useMemo(() => {
    return [
      {
        id: 'NOTIFICATION_CODE',
        name: UIHelper.buildTableColumn('Mã thông báo'),
        selector: (row) => row.notificationCode ?? row.notificationId,
        width: '140px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'TITLE',
        name: UIHelper.buildTableColumn('Tiêu đề'),
        selector: (row) => row?.title,
        minWidth: '320px',
        style: tableStyle.columns.normal,
        wrap: true,
      },
      {
        id: 'CONTENT',
        name: UIHelper.buildTableColumn('Nội dung'),
        selector: (row) => (
          <KTTooltip offset={[0, 0]} text={row?.message}>
            <span>
              {row?.message}
            </span>
          </KTTooltip>
        ),
        minWidth: '400px',
        style: tableStyle.columns.normal,
        wrap: true,
      },
      {
        id: 'RECEIVER',
        name: UIHelper.buildTableColumn('Người nhận'),
        selector: (row) => (
          <span
            className="cursor-pointer hover-opacity-60"
            onClick={() => {
              if (row?.metadata?.role === 'DRIVER') {
                Utils.openInNewTab(`${AppRoute.customerList}/${row?.user?.userId ?? ''}`);
              } else {
                const url = `/${row?.metadata?.role?.toLowerCase()}/${row?.user?.agencyId ?? ''}`;
                Utils.openInNewTab(url);
              }
            }}
          >
            <KTTooltip offset={[0, 0]} text={row?.user?.fullname ? row?.user?.fullname : ChargeHelper.Utils.getNotificationReceiverName(row?.metadata?.role)}>
              <span>
                {row?.user?.fullname ? row?.user?.fullname : ChargeHelper.Utils.getNotificationReceiverName(row?.metadata?.role)}
              </span>
            </KTTooltip>
          </span>
        ),
        width: '200px',
        style: tableStyle.columns.link,
      },
      {
        id: 'CREATED_AT',
        name: UIHelper.buildTableColumn('Ngày tạo'),
        selector: (row) => Utils.formatDateTimeToString(row?.createdAt, 'DD/MM/YYYY - HH:mm'),
        width: '150px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'CREATED_BY',
        name: UIHelper.buildTableColumn('Người tạo'),
        selector: (row) => {
          return (
            <KTTooltip offset={[0, 0]} text={row?.createdBy?.fullname ?? row?.name}>
              <span
                className="cursor-pointer hover-opacity-60"
              >
                {row?.creator?.fullname}
              </span>
            </KTTooltip>
          );
        },
        minWidth: '200px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'LINK',
        name: UIHelper.buildTableColumn('Liên kết đến'),
        selector: (row) => {
          return (
            <KTTooltip offset={[0, 0]} text={row?.metadata?.link}>
              <span
                className="cursor-pointer hover-opacity-60"
                onClick={() => Utils.openInNewTab(row?.metadata?.link)}
              >
                {row?.metadata?.link}
              </span>
            </KTTooltip>
          );
        },
        minWidth: '250px',
        style: tableStyle.columns.link,
      },
      {
        id: 'CATEGORY',
        name: UIHelper.buildTableColumn('Danh mục'),
        selector: (row) => ChargeHelper.Utils.getNotificationCategoryName(row?.category),
        minWidth: '100px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'STATUS',
        name: UIHelper.buildTableColumn('Trạng thái'),
        selector: (row) => {
          const status = row?.public
            ? row?.isSent
              ? ChargeConstants.NotificationStatus.SENT
              : ChargeConstants.NotificationStatus.SCHEDULED
            : ChargeConstants.NotificationStatus.HIDDEN;
          return (
            <KTLabel
              text={ChargeHelper.Utils.getNotificationStatusName(status)}
              isInline={true}
              size={KTLabelSize.medium}
              additionalClassName={`label-light-${ChargeHelper.Utils.getNotificationStatusColor(
                status
              )}`}
            />
          );
        },
        width: '130px',
      },
      {
        id: 'TIME',
        name: UIHelper.buildTableColumn('Ngày gửi'),
        selector: (row) => Utils.formatDateTimeToString(row?.releaseDate, 'DD/MM/YYYY - HH:mm'),
        width: '150px',
        style: tableStyle.columns.normal,
      },
    ];
  }, []);
  const [fullColumnIds, setFullColumnIds] = useState(
    fullColumns?.map((row) => row?.id).filter(Boolean) || []
  );
  const windowSize = useWindowSize();
  const {
    columnIds,
    columns,
    onChangeCheckboxValue,
    resetDefaultTableColumn,
    onSelectAll,
    onUnSelectAll,
    fullColumnsSorted,
    fullColumnsIdSorted,
    saveChanges,
  } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);

  // MARK: ---- FUNCTIONS ----
  const handleRefresh = () => {
    setFilter({ ...ChargeHelper.Filter.systemLogList });
  };

  return (
    <div>
      {UIHelper.buildCard(
        <div className="pb-4">
          {/* header */}
          <div className="px-4 pt-4 pb-4 d-flex align-items-center justify-content-between">
            {/* title */}
            <div className="d-flex flex-column">
              <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                Danh sách thông báo hệ thống
              </span>
              <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                Tổng:{' '}
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                  {Utils.formatNumber(total)} thông báo
                </span>
              </span>
            </div>

            {/* actions */}
            <div></div>
          </div>

          {/* table */}
          <div className="">
            {/* filter */}
            <div className="row m-0 p-0 px-2">
              {/* tim kiem */}
              <div className="col-xl-3 col-md-4 col-6 mb-4 px-2">
                {UIHelper.buildFormLabel('Tìm kiếm')}
                <KTFormSearch
                  name="search"
                  placeholder="Theo mã thông báo, tiêu đề, nội dung..."
                  value={filter.q}
                  onSubmit={(text) => {
                    ChargeHelper.Filter.systemLogList = {
                      ...ChargeHelper.Filter.systemLogList,
                      q: text,
                      page: 0,
                    };
                    handleRefresh();
                  }}
                />
              </div>
              {/* loc theo trang thai */}
              <div className="col mb-4 px-2">
                {UIHelper.buildFormLabel('Trạng thái')}
                <KTBSDropdown
                  name="status"
                  toggleElement={
                    <button
                      className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                      type="button"
                    >
                      {_.find(ChargeHelper.Data.notificationStatusOptions, { value: filter?.status })
                        ?.name ?? 'Tất cả'}
                    </button>
                  }
                  selectedValue={filter?.status}
                  dropdownMenuItems={[
                    { name: 'Tất cả', value: '' },
                    ...ChargeHelper.Data.notificationStatusOptions,
                  ]}
                  dropdownMenuClassName="overflow-auto w-100"
                  dropdownMenuStyle={{ maxHeight: '30vh' }}
                  onChange={(value) => {
                    ChargeHelper.Filter.systemLogList = {
                      ...ChargeHelper.Filter.systemLogList,
                      status: value,
                      page: 0,
                    };
                    handleRefresh();
                  }}
                  isDropdownForm={true}
                />
              </div>
              {/* ngay gui */}
              <div className="col mb-4 px-2">
                {UIHelper.buildFormLabel('Ngày gửi')}
                <CustomDateRangePicker
                  toggleElementBorderRadius={5}
                  initialValue={filter?.dateFilter}
                  onPressSave={(selectedValue, startDate, endDate) => {
                    ChargeHelper.Filter.systemLogList = {
                      ...ChargeHelper.Filter.systemLogList,
                      dateFilter: selectedValue,
                      from: startDate,
                      to: endDate,
                      page: 0,
                    };
                    handleRefresh();
                  }}
                />
              </div>
              {/* danh muc */}
              <div className="col mb-4 px-2">
                {UIHelper.buildFormLabel('Danh mục')}
                <KTBSDropdown
                  name="category"
                  toggleElement={
                    <button
                      className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                      type="button"
                    >
                      {_.find(
                        ChargeHelper.Data.notificationCategoryOptions,
                        (item) => item.value === ChargeHelper.Filter.systemLogList?.category
                      )?.name ?? 'Tất cả'}
                    </button>
                  }
                  selectedValue={ChargeHelper.Filter.systemLogList?.category}
                  dropdownMenuItems={[
                    { name: 'Tất cả', value: '' },
                    ...ChargeHelper.Data.notificationCategoryOptions,
                  ]}
                  dropdownMenuClassName="overflow-auto w-100"
                  dropdownMenuStyle={{ maxHeight: '30vh' }}
                  onChange={(value) => {
                    ChargeHelper.Filter.systemLogList = {
                      ...ChargeHelper.Filter.systemLogList,
                      category: value,
                      page: 0,
                    };
                    handleRefresh();
                  }}
                  isDropdownForm={true}
                />
              </div>
              {/* thiet lap cot */}
              <div className="col mb-4 px-2 d-flex align-items-end gap-3">
                {/* bo loc */}
                {/* <ButtonFilterDropdown
                  dropdownContent={
                    <ChargeVehicleListFilterView
                      onCancel={() => {
                        handleRefresh();
                      }}
                      onApply={() => {
                        handleRefresh();
                      }}
                      initialSelectedBrand={selectedVehicleBrand}
                      initialSelectedModel={selectedVehicleModel}
                      onSelectedBrandChange={(value) => {
                        setSelectedVehicleBrand(value);
                      }}
                      onSelectedModelChange={(value) => {
                        setSelectedVehicleModel(value);
                      }}
                    />
                  }
                /> */}
                <DropdownTableConfigColumn
                  columnIds={columnIds}
                  fullColumns={fullColumns}
                  fullColumnIds={fullColumnsIdSorted}
                  onChangeCheckboxValue={onChangeCheckboxValue}
                  resetDefaultTableColumn={resetDefaultTableColumn}
                  onChangeFullColumnIds={(ids) => {
                    setFullColumnIds(ids);
                    const key = prefKey + '_FULL_COLUMN_IDS';
                    localStorage.setItem(key, ids.join('|'));
                  }}
                  onSelectAll={onSelectAll}
                  onUnSelectAll={onUnSelectAll}
                  saveChanges={saveChanges}
                />
              </div>
            </div>

            <div className="px-4">
              <KTTable
                columns={columns}
                items={systemLogList}
                selectableRows={false}
                striped={false}
                compactGrid={false}
                onClickedRow={(row) => { }}
                onSelectedRowsChanged={(rows) => { }}
                onDoubleClickedRow={(row) => { }}
                noDataComponent={UIHelper.buildEmptyNoData(
                  'Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.'
                )}
                hasActionColumn={false}
              />
            </div>

            {/* pagination */}
            <div className="px-4 mt-2">
              <KTPagination
                currentPage={filter.page + 1}
                rowCount={total}
                rowsPerPage={filter.limit}
                currentItemInPageCount={systemLogList?.length}
                onChangePage={(newPage) => {
                  const iNewPage = parseInt(newPage);
                  ChargeHelper.Filter.systemLogList = {
                    ...ChargeHelper.Filter.systemLogList,
                    page: iNewPage - 1,
                  };
                  handleRefresh();
                }}
                onChangeRowsPerPage={(newRowsPerPage) => {
                  const iNewRowsPerPage = parseInt(newRowsPerPage);
                  ChargeHelper.Filter.systemLogList = {
                    ...ChargeHelper.Filter.systemLogList,
                    limit: iNewRowsPerPage,
                    page: 0,
                  };
                  handleRefresh();
                }}
                loading={false}
                color={KTPaginationColor.success}
                appendText="thông báo"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SystemLogListView;
